/*
 * Copyright (C) 2026 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import { saveAs } from 'file-saver';
import { ProvisionedTerminal } from '../../../../shared/entities/provisionedTerminal';
import { todayAsString } from '../../../utils/DateTimeUtils';

// Windows reserved device names that cannot be used as filenames
const WINDOWS_RESERVED_FILENAMES = /^(CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])$/i;

/**
 * Sanitize a string for safe filesystem filename across operating systems.
 */
export const sanitizeFilename = (name?: string): string => {
  if (!name || !name.trim()) {
    return 'terminal';
  }
  const sanitized = name.trim().replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 100);
  return WINDOWS_RESERVED_FILENAMES.test(sanitized) ? `${sanitized}_` : sanitized;
};

/**
 * Export an array of provisioned terminals as a downloadable JSON file.
 *
 * @param terminals List of terminals to serialize and download
 * @param filename Optional custom filename; defaults to terminals_YYYY-MM-DD.json
 */
export const exportTerminalsAsJson = (
  terminals: ProvisionedTerminal[],
  filename?: string,
): void => {
  const name = filename ?? `terminals_${todayAsString()}.json`;
  const jsonString = JSON.stringify(terminals, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  saveAs(blob, name);
};

/**
 * Export a single provisioned terminal as a downloadable JSON file wrapped in an array.
 * Standardizes payload format so ingestion parsers always receive an array of objects.
 *
 * @param terminal The single terminal to serialize and download
 * @param filename Optional custom filename; defaults to {displayName}.json
 */
export const exportTerminalAsJson = (
  terminal: ProvisionedTerminal,
  filename?: string,
): void => {
  exportTerminalsAsJson(
    [terminal],
    filename ?? `${sanitizeFilename(terminal.displayName)}.json`,
  );
};
